<template lang="html">
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div v-if="reconciledStatements" class="d-flex align-center">
            <v-icon large left color="white">mdi-bank-check</v-icon>
            <p class="subtitle-1 my-0">{{ reconciledStatements.Count }} reconciled statements</p>
            <v-divider vertical class="mx-3 my-1" color="white"></v-divider>
            <span class="subtitle-1">{{ totalAmount }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <PersonSearch
          v-model="personId"
          label="Search Person"
          :hide-details="true"
          :dark="true"
          @change="handleFilterChange"
        />
      </v-card-title>

      <!-- Filters Row -->
      <v-card-text class="py-2 px-4 tertiary">
        <v-row align="center" no-gutters>
          <!-- From Date -->
          <v-col cols="12" sm="6" md="4" class="pr-2">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleFromDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                @input="fromDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- To Date -->
          <v-col cols="12" sm="6" md="4" class="px-2">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleToDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                @input="toDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Clear Filters Button -->
          <v-col cols="12" sm="6" md="4" class="pl-2 text-right">
            <v-btn
              v-if="hasActiveFilters"
              text
              small
              color="secondary"
              @click="clearAllFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="displayItems"
        :loading="isLoading"
        :search="search"
        :server-items-length="reconciledStatements.Count || 0"
        :options.sync="tableOptions"
        @update:options="updateOptions"
        hide-default-footer
        class="elevation-0"
        no-data-text="No reconciled statements found"
      >
        <template v-slot:item.EntryDate="{ item }">
          {{ item.EntryDate | formatDate }}
        </template>

        <template v-slot:item.PostedDate="{ item }">
          {{ item.PostedDate | formatDate }}
        </template>

        <template v-slot:item.Amount="{ item }">
          <span :class="getAmountColor(item.Amount)">
            {{ formatCurrency(item.Amount) }}
          </span>
        </template>

        <template v-slot:item.SourceAccount="{ item }">
          <div v-if="item.SourceAccountName || item.SourceAccountNumber">
            <div>{{ item.SourceAccountName }}</div>
            <div class="caption grey--text">
              {{ item.SourceAccountSortCode }} {{ item.SourceAccountNumber }}
            </div>
          </div>
        </template>

        <template v-slot:item.Person="{ item }">
          <div v-if="item.ResolvedToPersonName">
            {{ item.ResolvedToPersonName }}
            <div class="caption grey--text">{{ item.ResolvedToPersonBranchName }}</div>
          </div>
        </template>

        <template v-slot:item.State="{ item }">
          <v-chip small :color="getStateColor(item.State)" dark>
            {{ item.State }}
          </v-chip>
        </template>
      </v-data-table>

        <!-- Custom Pagination Controls -->
        <v-card-actions class="pt-0">
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <!-- Items per page selector -->
            <span class="grey--text caption mr-2">Items per page:</span>
            <v-select
              :value="localItemsPerPage"
              :items="[10, 25, 50]"
              @change="handleItemsPerPageChange"
              hide-details
              dense
              style="max-width: 60px"
              class="mr-4"
            ></v-select>

            <!-- Page info -->
            <span class="mx-2 caption">
              {{ pageStart }}-{{ pageEnd }} of {{ reconciledStatements.Count || 0 }}
            </span>

            <!-- Page navigation buttons -->
            <v-btn
              icon
              small
              :disabled="localPage === 1 || isLoading"
              @click="handlePageChange(localPage - 1)"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              :disabled="localPage >= totalPages || isLoading"
              @click="handlePageChange(localPage + 1)"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import PersonSearch from '@/components/CloudSearch/PersonSearch.vue'

export default {
  components: {
    PersonSearch
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({ page: 1, itemsPerPage: 10 })
    }
  },
  data() {
    return {
      search: '',
      localPage: 1,
      localItemsPerPage: 10,
      isPending: false,
      tableOptions: {},
      headers: [
        { text: 'Reference', value: 'Reference', sortable: false },
        { text: 'Entry Date', value: 'EntryDate', sortable: false },
        { text: 'Posted Date', value: 'PostedDate', sortable: false },
        { text: 'Amount', value: 'Amount', sortable: false },
        { text: 'Bank Account', value: 'BankAccountName', sortable: false },
        { text: 'Source Account', value: 'SourceAccount', sortable: false },
        { text: 'Person', value: 'Person', sortable: false },
        { text: 'Tenancy', value: 'TenancyReference', sortable: false },
        { text: 'State', value: 'State', sortable: false }
      ],
      // Filter data
      personId: '',
      fromDate: '',
      toDate: '',
      fromDateMenu: false,
      toDateMenu: false
    }
  },
  methods: {
    getAmountColor(amount) {
      return amount >= 0 ? 'green--text font-weight-medium' : 'red--text font-weight-medium'
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
    },
    getStateColor(state) {
      const stateColors = {
        'Reconciled': 'green',
        'Pending': 'orange',
        'Failed': 'red',
        'Duplicate': 'grey'
      }
      return stateColors[state] || 'blue-grey'
    },
    handlePageChange(newPage) {
      if (this.isPending || newPage === this.localPage) return
      
      this.isPending = true
      this.localPage = newPage
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    handleItemsPerPageChange(newItemsPerPage) {
      if (this.isPending || newItemsPerPage === this.localItemsPerPage) return
      
      this.isPending = true
      this.localItemsPerPage = newItemsPerPage
      this.localPage = 1 // Reset to first page when changing items per page
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    emitPaginationChange() {
      // Only emit if search is empty (server-side pagination)
      if (this.search === "") {
        this.$emit('paginate', this.localPage, this.localItemsPerPage, {
          personId: this.personId,
          fromDate: this.fromDate,
          toDate: this.toDate
        });
      }
    },
    updateOptions() {
      // This method is called when table options change
      // We don't need to do anything here since we're using custom pagination
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.localPage = 1;
      this.$emit('filter-change', {
        personId: this.personId,
        fromDate: this.fromDate,
        toDate: this.toDate
      });
    },
    handleFromDateClear() {
      this.fromDate = '';
      this.handleFilterChange();
    },
    handleToDateClear() {
      this.toDate = '';
      this.handleFilterChange();
    },
    clearAllFilters() {
      this.personId = '';
      this.fromDate = '';
      this.toDate = '';
      this.handleFilterChange();
    }
  },
  computed: {
    ...mapState({
      reconciledStatements: state => state.UnreconciledPayments.reconciledStatementsRecords
    }),
    hasActiveFilters() {
      return this.personId || this.fromDate || this.toDate;
    },
    totalAmount() {
      if (!this.reconciledStatements || !this.reconciledStatements.Data) return this.formatCurrency(0)
      
      const total = this.reconciledStatements.Data.reduce((sum, item) => sum + (item.Amount || 0), 0)
      return this.formatCurrency(total)
    },
    displayItems() {
      if (!this.reconciledStatements || !this.reconciledStatements.Data) return []
      
      // For server-side pagination, we show all data received
      // Search is handled client-side on the current page only
      if (!this.search) {
        return this.reconciledStatements.Data
      }
      
      const searchLower = this.search.toLowerCase()
      return this.reconciledStatements.Data.filter(item => {
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      })
    },
    totalPages() {
      if (!this.reconciledStatements || !this.reconciledStatements.Count) return 1
      return Math.ceil(this.reconciledStatements.Count / this.localItemsPerPage)
    },
    pageStart() {
      if (!this.reconciledStatements || !this.reconciledStatements.Count || this.reconciledStatements.Count === 0) return 0
      return ((this.localPage - 1) * this.localItemsPerPage) + 1
    },
    pageEnd() {
      if (!this.reconciledStatements || !this.reconciledStatements.Count) return 0
      const end = this.localPage * this.localItemsPerPage
      return end > this.reconciledStatements.Count ? this.reconciledStatements.Count : end
    }
  },
  watch: {
    pagination: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localPage = val.page || 1
          this.localItemsPerPage = val.itemsPerPage || 10
        }
      }
    }
  },
  filters: {
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      })
    }
  }
}
</script>

<style lang="css" scoped>
.position-relative {
  position: relative;
}
</style>